import { useState } from "react";
import AnMultipleSuggestionInputBox from "../../../input_utils/AnMultipleSuggestionBox";
import { Popup } from "../../../../design_system/components/Popup/Popup";
import { MENTION_TYPES } from "../../../utils/AllMentionTypes";
import { Radio } from "../../../../design_system/components/SelectionControls/Radio";
import PostPreview from "./PostPreview";
import { InputBox } from "../../../../design_system/components/Input Fields/InputBox";
import { InputArea } from "../../../../design_system/components/Input Fields/InputArea";
import ConditionalRender from "../../../../ConditionalRender";
import { SingleDateFilterDropdown } from "../../../admin_views/teams/singleDateFilter";
import useExternalArticleFetch from "../../../custom_hooks/useExternalArticleFetch";
import { dateFormator, datepicker_input_format } from "../../../utils/dateUtils";
import { apiAgent } from "../../../../axiosConfig";

const DEFAULT_LABELS = { popupName: "Post Article", submitBtn: "Publish Article" }

const defaultArticle = { description: "", url: "", external_url: "", image_url: "", title: "", published_at: "", sitename: "", prospects: [], mentioned_for: "" };
const initialArticleState = (aggregateData) => ({
    ...defaultArticle,
    ...aggregateData?.sharable_article,
    prospects: aggregateData?.prospects || [],
    mentioned_for: aggregateData?.mentioned_for || "",
});

const initialPopupState = { isLoading: false, errors: {}, showDateDropdown: false, showPreview: false, fetchingDetails: false };

const ManualPostPopup = ({
    onCloseCallback,
    labels = {},
    aggregateData,
    onPublishCallback,
    publish_url,
    ...props
}) => {
    const popupLabels = { ...DEFAULT_LABELS, ...labels }
    const [article, setArticle] = useState(() => initialArticleState(aggregateData));
    const [popupState, setPopupState] = useState(initialPopupState);
    const updatePopupState = (payload) => setPopupState(prev => ({ ...prev, ...payload }));

    const setArticleField = (key, value) => {
        setArticle(prev => ({ ...prev, [key]: value }));
        updatePopupState({ errors: {} });
    };

    const responseCallback = (type, response) => {
        switch (type) {
            case "LOADING": {
                updatePopupState({ fetchingDetails: true })
                break;
            }
            case "SUCCESS": {
                let external_data = response.data.data.page_attr || {}
                setArticle(prev => ({
                    ...prev,
                    ...external_data,
                    url: external_data.external_url || prev.url,
                    published_at: external_data.published_time || prev.published_at || "",
                }));
                updatePopupState({ fetchingDetails: false })
                break;
            }
            case "ERROR":
            default: {
                updatePopupState({ fetchingDetails: false })
                break;
            }
        }
    }

    useExternalArticleFetch("/posts/fetch_external_data", article.external_url, responseCallback);

    const handlePostPublish = () => {
        const tagged_prospect_ids = article.prospects.map((item) => item.id);
        let errors = {};
        if (tagged_prospect_ids.length === 0) errors["publish_error_message"] = "It is mandatory to tag prospects to create post";
        if (!article.description) errors["description"] = "Description can't be empty";
        if (!article.url) errors["url"] = "Article url can't be empty";
        if (Object.keys(errors).length > 0) {
            updatePopupState({ errors });
            return;
        }
        updatePopupState({ isLoading: true, errors: {} });
        let data_params = {
            tagged_prospect_ids,
            mentioned_for: article.mentioned_for,
            shared_external_url: article.url,
            shared_published_time: datepicker_input_format(new Date(article.published_at)),
            shared_title: article.title,
            shared_description: article.description,
            shared_image_url: article.image_url,
            shared_sitename: article.sitename,
            check_pub_guideline_ack: !!props.checkGuidelineAcknowledgement,
        }
        apiAgent({
            method: "post",
            url: publish_url,
            data: data_params
        }).then(res => {
            onCloseCallback()
            updatePopupState({ isLoading: false, errors: {} })
            onPublishCallback?.(aggregateData, res.data)
        }).catch(err => {
            if (err.response?.data?.error === 'ack_failed') props.renderGuidelinesUpdateWarning(err.response.data.data);
            updatePopupState({ isLoading: false, errors: { submitError: "Post was not created successfully" } })
        })
    }

    return (
        <Popup
            name={popupLabels.popupName}
            handleOnClosePopup={onCloseCallback}
            maxWidth={900}
            customContentStyle={{ padding: "0px" }}
            primaryAction={{
                label: popupLabels.submitBtn,
                onClick: handlePostPublish,
                isLoading: popupState.isLoading,
                error: popupState.errors?.publish_error_message,
                helperText: popupState.errors?.publish_error_message,
                showHelperText: true
            }}
            secondaryAction={{
                label: "Cancel",
                onClick: onCloseCallback
            }}
        >
            <div className="flex publish-popup__body">

                {/* ─── Left: Prospect search + Mention types ─── */}
                <div className="flex flex-col w-full overflow-hidden publish-popup__left popup-col">
                    <div className="publish-popup__section">
                        <AnMultipleSuggestionInputBox
                            label={<span className="body-s-semibold color-text-secondary">Add prospects related to news article</span>}
                            inputClass="an_search_box"
                            placeholder="Search Prospects"
                            suggestionUrl="/prospects/search?team_search=true"
                            selectedOptions={article.prospects}
                            callback={(options) => setArticle(prev => ({ ...prev, prospects: [...options] }))}
                            theme="tagged_options"
                        />
                    </div>
                    <>
                        <div className="newsfeed__divider publish-popup__section-divider"></div>
                        <div className="body-s-semibold publish-popup__mention-text">
                            Mentioned for:{" "}
                            {article.mentioned_for && (
                                <span className="body-m-semibold">{article.mentioned_for}</span>
                            )}
                        </div>
                        <div className="flex-1 scroll-y min-h-0 publish-popup__mention-scroll popup-scroll-section">
                            {MENTION_TYPES.map((mention_type, index) => (
                                <div key={index} className={index > 0 ? "publish-popup__mention-item" : ""}>
                                    <Radio
                                        label={mention_type.text}
                                        name="mention"
                                        value={mention_type.value}
                                        checked={article.mentioned_for === mention_type.value}
                                        onChange={() => setArticle(prev => ({ ...prev, mentioned_for: mention_type.value }))}
                                    />
                                </div>
                            ))}
                        </div>
                    </>
                </div>

                {/* ─── Vertical divider ─── */}
                <div className="publish-popup__vertical-divider"></div>

                {/* ─── Right: Article fields ─── */}
                <div className="flex-1 publish-popup__right">
                    <InputBox
                        label="Article link"
                        showLabel
                        Size="Large"
                        error={popupState.errors?.url}
                        helperText={popupState.errors?.url}
                        showHelperText={!!popupState.errors?.url}
                        customStyle={{ maxWidth: 508 }}
                        type="text"
                        value={article.url}
                        handleOnChange={(e) => setArticle(prev => ({ ...prev, url: e.target.value, external_url: e.target.value }))}
                        placeholder="Article url"
                    />
                    <div className="flex items-start gap-m publish-popup__date-row">
                        <div className="publish-popup__date-wrapper">
                            <InputBox
                                label="Article Published date"
                                showLabel
                                Size="Large"
                                type="text"
                                value={dateFormator(new Date(article.published_at), { type: "input-format" }, "-")}
                                onClick={() => updatePopupState({ showDateDropdown: true })}
                                placeholder="dd/mm/yyyy"
                                readOnly
                                innerInputStyle={{ cursor: 'pointer' }}
                            />
                            <ConditionalRender if={popupState.showDateDropdown}>
                                <SingleDateFilterDropdown
                                    toggleDropDown={() => updatePopupState({ showDateDropdown: !popupState.showDateDropdown })}
                                    currDate={new Date(article.published_at || new Date())}
                                    setCurrDate={(date) => {
                                        setArticleField("published_at", dateFormator(new Date(date), { type: "input-format" }, "-"));
                                        updatePopupState({ showDateDropdown: false });
                                    }}
                                />
                            </ConditionalRender>
                        </div>
                    </div>

                    <InputArea
                        label="Article Title"
                        showLabel
                        Size="Medium"
                        customStyle={{ marginTop: "var(--space-m)" }}
                        value={article.title || ""}
                        handleOnChange={(e) => setArticleField("title", e.target.value)}
                    />

                    <InputArea
                        label="Article Description"
                        showLabel
                        Size="Large"
                        error={popupState.errors?.description}
                        helperText={popupState.errors?.description}
                        showHelperText={!!popupState.errors?.description}
                        customStyle={{ marginTop: "var(--space-m)" }}
                        value={article.description || ""}
                        handleOnChange={(e) => setArticleField("description", e.target.value)}
                    />
                </div>
            </div>

            {/* ─── Post preview section ─── */}
            <PostPreview
                article={article}
                isOpen={popupState.showPreview}
                onToggle={() => updatePopupState({ showPreview: !popupState.showPreview })}
            />
        </Popup>
    )
}

export default ManualPostPopup
